import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  Button,
  Upload,
  message,
  Space,
  Typography,
  InputNumber,
  Select,
  Input,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

import {
  StockData,
  TradeConfirmationRow,
  WithdrawOfferRow,
  BondDistributionFile,
} from './types';
import { ceilTo4Decimals } from './utils';
import {
  parseWithdrawOffer,
  parseTradeConfirmation,
  combineTradeConfirmations,
  parseBondDistribution,
} from './parsers';
import { downloadProcessedOffer } from './generators';
import { DEFAULT_BOND_METADATA } from './utils';
import {
  isRefundDistributionBackendEnabled,
  saveRefundDistributionFileToBackend,
} from './distributionStorage';

const { Text } = Typography;

const sectionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #f0f0f0',
  borderRadius: 12,
  padding: 24,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 20,
};

const stepBadgeStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: '#1677ff',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 600,
  flexShrink: 0,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: '#262626',
  marginBottom: 8,
};

const fieldHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#8c8c8c',
  display: 'block',
  marginTop: 6,
};
const REFUND_DISTRIBUTION_UI_STORAGE_KEY =
  'refund-bond-distribution-ui-v3';
const LEGACY_REFUND_DISTRIBUTION_UI_STORAGE_KEYS = [
  'refund-bond-distribution-ui-v1',
  'refund-bond-distribution-ui-v2',
];

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every((item) => typeof item === 'string');

const sanitizeDistributionSlotsByBond = (
  value: unknown
): Record<string, string[]> => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value)
  ) {
    return {};
  }

  return Object.entries(value).reduce<
    Record<string, string[]>
  >((acc, [bondName, slotIds]) => {
    if (!isStringArray(slotIds)) return acc;
    acc[bondName] = slotIds;
    return acc;
  }, {});
};

const PREFERRED_BOND_OPTIONS = [
  'Emerald',
  'Лэнд 2.4',
  'Цогц зээл',
  'АБТС',
  'Альфа',
  'АМУ 1',
  'АМУ 2',
  'Нөмөр',
  'Нөмөр 8.3',
  'Нөмөр 8.5',
  'Платин',
  'Сэндли',
  'Хүннү',
  'Шунхлай',
  'Шунхлай 2',
  'Шунхлай 3.1',
  'Шунхлай 3.3',
  'Гялс төгрөг',
  'Лэнд МН',
  'National Investment',
  'Нео 1.1',
  'Нео 1.2',
  'Нео 1.3',
  'Нео 1.4',
  'Нео 1.5',
  'GSB-ABS ХБҮЦ',
];

const BOND_DISTRIBUTION_OPTIONS = (() => {
  const allBonds = Array.from(
    new Set(
      Object.keys(DEFAULT_BOND_METADATA).filter(
        (name) => name !== 'ХБҮЦ' && name !== 'GSB-ABS'
      )
    )
  );
  const preferred = PREFERRED_BOND_OPTIONS.filter((name) =>
    allBonds.includes(name)
  );
  const rest = allBonds
    .filter((name) => !preferred.includes(name))
    .sort((a, b) => a.localeCompare(b, 'mn'));
  return [...preferred, ...rest];
})();

const loadDistributionUiState = () => {
  if (typeof window === 'undefined') {
    return null as null | {
      selectedDistributionBond: string;
      activeDistributionBonds: string[];
      distributionSlotsByBond: Record<string, string[]>;
      nextDistributionSlotId: number;
      customDistributionBondOptions: string[];
    };
  }
  try {
    const raw = window.localStorage.getItem(
      REFUND_DISTRIBUTION_UI_STORAGE_KEY
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<
      string,
      unknown
    >;
    return {
      selectedDistributionBond:
        typeof parsed.selectedDistributionBond ===
        'string'
          ? parsed.selectedDistributionBond
          : '',
      activeDistributionBonds: isStringArray(
        parsed.activeDistributionBonds
      )
        ? parsed.activeDistributionBonds
        : [],
      distributionSlotsByBond:
        sanitizeDistributionSlotsByBond(
          parsed.distributionSlotsByBond
        ),
      nextDistributionSlotId:
        typeof parsed.nextDistributionSlotId ===
          'number' &&
        Number.isFinite(parsed.nextDistributionSlotId)
          ? parsed.nextDistributionSlotId
          : 2,
      customDistributionBondOptions: isStringArray(
        parsed.customDistributionBondOptions
      )
        ? parsed.customDistributionBondOptions
        : [],
    };
  } catch {
    return null;
  }
};

const buildUploadedDistributionBondOrder = (
  files: BondDistributionFile[]
): string[] =>
  Array.from(
    new Set(
      files
        .map((file) => String(file.bondName || '').trim())
        .filter(Boolean)
    )
  );

const buildDistributionSlotsFromUploads = (
  files: BondDistributionFile[],
  fallback: Record<string, string[]> = {}
): Record<string, string[]> => {
  const next: Record<string, string[]> = {};

  files.forEach((file) => {
    const bondName = String(file.bondName || '').trim();
    const slotId = String(file.uploadSlotId || 'quarter-1').trim();
    if (!bondName) return;
    if (!next[bondName]) next[bondName] = [];
    if (!next[bondName].includes(slotId)) next[bondName].push(slotId);
  });

  Object.entries(fallback).forEach(([bondName, slotIds]) => {
    if (!next[bondName]) return;
    slotIds.forEach((slotId) => {
      if (!next[bondName].includes(slotId)) next[bondName].push(slotId);
    });
  });

  Object.keys(next).forEach((bondName) => {
    next[bondName].sort((a, b) => {
      const aNum = Number(a.replace('quarter-', '')) || 0;
      const bNum = Number(b.replace('quarter-', '')) || 0;
      return aNum - bNum;
    });
  });

  return next;
};

const persistDistributionUiState = (state: {
  selectedDistributionBond: string;
  activeDistributionBonds: string[];
  distributionSlotsByBond: Record<string, string[]>;
  nextDistributionSlotId: number;
  customDistributionBondOptions: string[];
}) => {
  if (typeof window === 'undefined') return;

  const fullPayload = JSON.stringify(state);
  try {
    window.localStorage.setItem(
      REFUND_DISTRIBUTION_UI_STORAGE_KEY,
      fullPayload
    );
    return;
  } catch (error) {
    console.warn(
      'Failed to persist full refund distribution UI state',
      error
    );
  }

  try {
    LEGACY_REFUND_DISTRIBUTION_UI_STORAGE_KEYS.forEach(
      (key) => {
        window.localStorage.removeItem(key);
      }
    );
  } catch {}

  const compactPayload = JSON.stringify({
    selectedDistributionBond:
      state.selectedDistributionBond,
    activeDistributionBonds:
      state.activeDistributionBonds,
    nextDistributionSlotId:
      state.nextDistributionSlotId,
    customDistributionBondOptions:
      state.customDistributionBondOptions,
  });

  try {
    window.localStorage.setItem(
      REFUND_DISTRIBUTION_UI_STORAGE_KEY,
      compactPayload
    );
  } catch (error) {
    console.warn(
      'Failed to persist compact refund distribution UI state',
      error
    );
    try {
      window.localStorage.removeItem(
        REFUND_DISTRIBUTION_UI_STORAGE_KEY
      );
    } catch {}
  }
};

interface Part1Props {
  combinedTC: TradeConfirmationRow[];
  setCombinedTC: (tc: TradeConfirmationRow[]) => void;
  bondDistributionFiles: UploadFile[];
  setBondDistributionFiles: Dispatch<
    SetStateAction<UploadFile[]>
  >;
  bondDistributionData: BondDistributionFile[];
  setBondDistributionData: Dispatch<
    SetStateAction<BondDistributionFile[]>
  >;
  onContinue: () => void;
  showDistributionManager: boolean;
}

export const Part1 = ({
  combinedTC,
  setCombinedTC,
  bondDistributionFiles,
  setBondDistributionFiles,
  bondDistributionData,
  setBondDistributionData,
  onContinue,
  showDistributionManager,
}: Part1Props) => {
  const persistedUiState = loadDistributionUiState();
  const uploadedDistributionBonds =
    buildUploadedDistributionBondOrder(
      bondDistributionData
    );
  const [withdrawOfferFiles, setWithdrawOfferFiles] =
    useState<UploadFile[]>([]);
  const [
    tradeConfirmationFiles,
    setTradeConfirmationFiles,
  ] = useState<UploadFile[]>([]);
  const [processedOffers, setProcessedOffers] = useState<
    { fileName: string; data: StockData[] }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [commissionRate, setCommissionRate] =
    useState<number>(0.8);
  const [
    selectedDistributionBond,
    setSelectedDistributionBond,
  ] = useState<string>(
    uploadedDistributionBonds[0] ||
      persistedUiState?.selectedDistributionBond ||
      BOND_DISTRIBUTION_OPTIONS[0] ||
      ''
  );
  const [
    activeDistributionBonds,
    setActiveDistributionBonds,
  ] = useState<string[]>(
    uploadedDistributionBonds.length > 0
      ? uploadedDistributionBonds
      : persistedUiState?.activeDistributionBonds ||
          []
  );
  const [
    distributionSlotsByBond,
    setDistributionSlotsByBond,
  ] = useState<Record<string, string[]>>(
    () =>
      uploadedDistributionBonds.length > 0
        ? buildDistributionSlotsFromUploads(
            bondDistributionData,
            persistedUiState?.distributionSlotsByBond
          )
        : persistedUiState?.distributionSlotsByBond ||
            {}
  );
  const [
    nextDistributionSlotId,
    setNextDistributionSlotId,
  ] = useState(
    persistedUiState?.nextDistributionSlotId || 2
  );
  const [
    customDistributionBondOptions,
    setCustomDistributionBondOptions,
  ] = useState<string[]>(
    persistedUiState?.customDistributionBondOptions || []
  );
  const [newDistributionBondName, setNewDistributionBondName] =
    useState('');
  const visibleDistributionBond =
    selectedDistributionBond ||
    BOND_DISTRIBUTION_OPTIONS[0] ||
    '';
  const visibleDistributionSlots =
    isStringArray(
      distributionSlotsByBond[visibleDistributionBond]
    ) &&
    distributionSlotsByBond[visibleDistributionBond]
      .length > 0
      ? distributionSlotsByBond[
          visibleDistributionBond
        ]
      : ['quarter-1'];
  const allDistributionBondOptions = Array.from(
    new Set([
      ...BOND_DISTRIBUTION_OPTIONS,
      ...customDistributionBondOptions,
    ])
  );

  useEffect(() => {
    if (uploadedDistributionBonds.length === 0) return;
    setActiveDistributionBonds((prev) => {
      const merged = Array.from(
        new Set([
          ...uploadedDistributionBonds,
          ...prev.filter((bondName) =>
            uploadedDistributionBonds.includes(bondName)
          ),
        ])
      );
      return merged;
    });
    setDistributionSlotsByBond((prev) =>
      buildDistributionSlotsFromUploads(
        bondDistributionData,
        prev
      )
    );
    if (
      !uploadedDistributionBonds.includes(
        selectedDistributionBond
      )
    ) {
      setSelectedDistributionBond(
        uploadedDistributionBonds[0]
      );
    }
  }, [bondDistributionData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    persistDistributionUiState({
      selectedDistributionBond,
      activeDistributionBonds,
      distributionSlotsByBond,
      nextDistributionSlotId,
      customDistributionBondOptions,
    });
  }, [
    selectedDistributionBond,
    activeDistributionBonds,
    distributionSlotsByBond,
    nextDistributionSlotId,
    customDistributionBondOptions,
  ]);

  // Process offers with TC data
  const processWithdrawOffers = (
    offers: {
      fileName: string;
      data: WithdrawOfferRow[];
    }[],
    tcData: TradeConfirmationRow[]
  ) => {
    const tcMap = new Map(
      tcData.map((tc) => [tc.symbol, tc])
    );

    return offers.map(({ fileName, data }) => ({
      fileName,
      data: data.map((offer) => {
        const tc = tcMap.get(offer.symbol);

        if (!tc || offer.quantity === 0) {
          return {
            symbol: offer.symbol,
            name: offer.name,
            quantity: 0,
            price: 0,
            proceeds: 0,
            commission: 0,
          };
        }

        const quantity = ceilTo4Decimals(offer.quantity);
        const price = tc.unitPrice;
        const proceeds = quantity * price;
        const commission =
          proceeds * (commissionRate / 100);

        return {
          symbol: offer.symbol,
          name: tc.name || offer.name,
          quantity,
          price,
          proceeds:
            Math.round(proceeds * 1000000) / 1000000,
          commission:
            Math.round(commission * 1000000) / 1000000,
        };
      }),
    }));
  };

  const handleProcess = async () => {
    if (tradeConfirmationFiles.length === 0) {
      message.error('Trade Confirmation файл оруулна уу');
      return;
    }

    setLoading(true);

    try {
      const tcParsedPromises = tradeConfirmationFiles.map(
        (f) =>
          parseTradeConfirmation(f.originFileObj as File)
      );
      const tcParsed = await Promise.all(tcParsedPromises);
      const combinedTCData =
        combineTradeConfirmations(tcParsed);
      setCombinedTC(combinedTCData);

      if (withdrawOfferFiles.length > 0) {
        const offersParsedPromises = withdrawOfferFiles.map(
          async (f) => ({
            fileName: f.name,
            data: await parseWithdrawOffer(
              f.originFileObj as File
            ),
          })
        );
        const offersParsed = await Promise.all(
          offersParsedPromises
        );

        const processed = processWithdrawOffers(
          offersParsed,
          combinedTCData
        );
        setProcessedOffers(processed);
        message.success(
          'Trade Confirmation болон буцаалтын хүсэлтийн файлууд амжилттай боловсруулагдлаа'
        );
      } else {
        setProcessedOffers([]);
        message.success(
          'Trade Confirmation файл амжилттай боловсруулагдлаа. Part 2 руу үргэлжлүүлж болно.'
        );
      }
    } catch (error) {
      console.error(error);
      message.error('Файл боловсруулахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleBondDistributionUpload = async (
    file: File,
    bondName: string,
    slotId: string
  ) => {
    try {
      const parsedFile = await parseBondDistribution(file);
      const nextFile: BondDistributionFile = {
        ...parsedFile,
        fileName: file.name,
        bondName,
        uploadSlotId: slotId,
        data: parsedFile.data.map((row) => ({
          ...row,
          bondName,
        })),
      };
      const backendSaveResult =
        await saveRefundDistributionFileToBackend(
          nextFile
        );
      const fileUid = `bond-distribution:${bondName}:${slotId}`;

      setBondDistributionFiles((prev) => [
        ...prev.filter((item) => item.uid !== fileUid),
        {
          uid: fileUid,
          name: file.name,
          status: 'done',
          originFileObj: file,
        } as UploadFile,
      ]);

      setBondDistributionData((prev) => [
        ...prev.filter(
          (item) =>
            !(
              item.uploadSlotId === slotId &&
              item.bondName === bondName
            )
        ),
        nextFile,
      ]);

      if (backendSaveResult === 'saved') {
        message.success(
          `${bondName} тархалтын файл уншигдлаа (${nextFile.data.length} мөр)`
        );
      } else if (
        backendSaveResult === 'skipped' &&
        !isRefundDistributionBackendEnabled()
      ) {
        message.info(
          `${bondName} тархалтын файл local cache дээр хадгалагдлаа. Энэ localhost build дээр backend persistence идэвхгүй байна.`
        );
      } else {
        message.warning(
          `${bondName} тархалтын файл local cache дээр уншигдлаа. Backend save хийгдээгүй байна.`
        );
      }
    } catch (error) {
      console.error(error);
      message.error(
        'Тархалтын файл уншихад алдаа гарлаа'
      );
    }
  };

  const ensureBondSlot = (bondName: string) => {
    setDistributionSlotsByBond((prev) => {
      if (prev[bondName]?.length) return prev;
      return {
        ...prev,
        [bondName]: ['quarter-1'],
      };
    });
  };

  const addDistributionBond = (bondName: string) => {
    if (!bondName) return;
    ensureBondSlot(bondName);
    let added = false;
    setActiveDistributionBonds((prev) => {
      if (prev.includes(bondName)) return prev;
      added = true;
      return [...prev, bondName];
    });
    if (!added) {
      message.info(`${bondName} аль хэдийн нэмэгдсэн байна`);
    } else {
      message.success(`${bondName} нэмэгдлээ`);
    }
  };

  const addDistributionSlot = (bondName: string) => {
    const nextSlotId = `quarter-${nextDistributionSlotId}`;
    setDistributionSlotsByBond((prev) => ({
      ...prev,
      [bondName]: [
        ...(prev[bondName] || ['quarter-1']),
        nextSlotId,
      ],
    }));
    setNextDistributionSlotId((prev) => prev + 1);
  };

  const addCustomDistributionBondOption = () => {
    const bondName = String(newDistributionBondName || '').trim();
    if (!bondName) return;
    setCustomDistributionBondOptions((prev) =>
      prev.includes(bondName) ? prev : [...prev, bondName]
    );
    setSelectedDistributionBond(bondName);
    setNewDistributionBondName('');
    message.success(`${bondName} dropdown-д нэмэгдлээ`);
  };

  return (
    <Space
      direction="vertical"
      size={24}
      style={{ width: '100%' }}
    >
      {/* File Upload Section */}
      <section>
        <Space
          direction="vertical"
          size={20}
          style={{ width: '100%' }}
        >
          <div>
            <label style={fieldLabelStyle}>
              Буцаалтын хүсэлт
            </label>
            <Upload
              multiple
              accept=".xlsx,.xls"
              fileList={withdrawOfferFiles}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={({ fileList }) =>
                setWithdrawOfferFiles(fileList)
              }
            >
              <Button
                icon={
                  withdrawOfferFiles.length > 0 ? (
                    <CheckCircleOutlined />
                  ) : (
                    <UploadOutlined />
                  )
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  maxWidth: 360,
                  ...(withdrawOfferFiles.length > 0
                    ? {
                        borderColor: '#52c41a',
                        color: '#52c41a',
                      }
                    : {}),
                }}
              >
                {withdrawOfferFiles.length === 0
                  ? 'Файл сонгох'
                  : withdrawOfferFiles.length === 1
                  ? withdrawOfferFiles[0].name
                  : `${withdrawOfferFiles.length} файл сонгогдсон`}
              </Button>
            </Upload>
          </div>

          <div>
            <label style={fieldLabelStyle}>
              Trade Confirmation
            </label>
            <Upload
              multiple
              accept=".csv,.xlsx,.xls"
              fileList={tradeConfirmationFiles}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={({ fileList }) =>
                setTradeConfirmationFiles(fileList)
              }
            >
              <Button
                icon={
                  tradeConfirmationFiles.length > 0 ? (
                    <CheckCircleOutlined />
                  ) : (
                    <UploadOutlined />
                  )
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  maxWidth: 360,
                  ...(tradeConfirmationFiles.length > 0
                    ? {
                        borderColor: '#52c41a',
                        color: '#52c41a',
                      }
                    : {}),
                }}
              >
                {tradeConfirmationFiles.length === 0
                  ? 'Файл сонгох'
                  : tradeConfirmationFiles.length === 1
                  ? tradeConfirmationFiles[0].name
                  : `${tradeConfirmationFiles.length} файл сонгогдсон`}
              </Button>
            </Upload>
          </div>

          <div>
            <label style={fieldLabelStyle}>
              Шимтгэлийн хувь (Commission)
            </label>
            <InputNumber
              min={0}
              max={100}
              step={0.1}
              value={commissionRate}
              onChange={(value) =>
                setCommissionRate(value || 0.8)
              }
              addonAfter="%"
              style={{ width: 160 }}
            />
          </div>

          {showDistributionManager && (
            <div
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 10,
                padding: 20,
              }}
            >
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: 14,
                }}
              >
                Файл удирдлага
              </Text>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <Text style={{ fontSize: 13, color: '#595959' }}>
                  Бонд сонгох
                </Text>
                <Select
                  value={selectedDistributionBond}
                  onChange={(value) => {
                    setSelectedDistributionBond(value);
                    ensureBondSlot(value);
                  }}
                  style={{ width: 240 }}
                  showSearch
                  optionFilterProp="label"
                  options={allDistributionBondOptions.map(
                    (bondName) => ({
                      value: bondName,
                      label: bondName,
                    })
                  )}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Space style={{ padding: '0 8px 8px' }}>
                        <Input
                          placeholder="Шинэ бондын нэр"
                          value={newDistributionBondName}
                          onChange={(event) =>
                            setNewDistributionBondName(
                              event.target.value
                            )
                          }
                          onPressEnter={(event) => {
                            event.preventDefault();
                            addCustomDistributionBondOption();
                          }}
                          style={{ width: 200 }}
                        />
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={
                            addCustomDistributionBondOption
                          }
                        >
                          Нэмэх
                        </Button>
                      </Space>
                    </>
                  )}
                />
                <Button
                  icon={<PlusOutlined />}
                  onClick={() =>
                    addDistributionBond(
                      selectedDistributionBond
                    )
                  }
                >
                  Бонд нэмэх
                </Button>
              </div>

              <span
                style={{
                  ...fieldHintStyle,
                  marginTop: 10,
                }}
              >
                Dropdown-оос бонд сонгоод "Бонд нэмэх" дарж
                доор жагсаалтад оруулна.
              </span>

              <div style={{ marginTop: 18 }}>
                <Space
                  direction="vertical"
                  size={10}
                  style={{ width: '100%' }}
                >
                  {visibleDistributionSlots.map(
                    (slotId, index) => {
                      const slotFile =
                        bondDistributionFiles.find(
                          (file) =>
                            file.uid ===
                            `bond-distribution:${visibleDistributionBond}:${slotId}`
                        );
                      const slotDataFile =
                        bondDistributionData.find(
                          (file) =>
                            file.uploadSlotId === slotId &&
                            file.bondName ===
                              visibleDistributionBond
                        );
                      const isLastRow =
                        index ===
                        visibleDistributionSlots.length - 1;
                      return (
                        <div
                          key={`${visibleDistributionBond}-${slotId}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 13,
                              color: '#595959',
                              minWidth: 78,
                            }}
                          >
                            Quarter {index + 1}
                          </Text>
                          <Upload
                            accept=".xlsx,.xls"
                            showUploadList={false}
                            beforeUpload={(file) => {
                              void handleBondDistributionUpload(
                                file,
                                visibleDistributionBond,
                                slotId
                              );
                              return false;
                            }}
                          >
                            <Button
                              icon={
                                slotDataFile ? (
                                  <CheckCircleOutlined />
                                ) : (
                                  <UploadOutlined />
                                )
                              }
                              style={
                                slotDataFile
                                  ? {
                                      borderColor: '#52c41a',
                                      color: '#52c41a',
                                    }
                                  : undefined
                              }
                            >
                              {slotDataFile?.fileName ||
                                slotFile?.name ||
                                'Тархалт'}
                            </Button>
                          </Upload>
                          {isLastRow && (
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() =>
                                addDistributionSlot(
                                  visibleDistributionBond
                                )
                              }
                            >
                              Улирал нэмэх
                            </Button>
                          )}
                        </div>
                      );
                    }
                  )}
                </Space>
              </div>
            </div>
          )}

          <div style={{ paddingTop: 8 }}>
            <Space size={12}>
              <Button
                type="primary"
                size="large"
                onClick={handleProcess}
                loading={loading}
                style={{ minWidth: 160 }}
              >
                Боловсруулах
              </Button>
              {processedOffers.map((offer, index) => (
                <Button
                  key={index}
                  size="large"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadProcessedOffer(offer)}
                >
                  Excel татах
                </Button>
              ))}
            </Space>
          </div>

          {(processedOffers.length > 0 ||
            combinedTC.length > 0) && (
            <div>
              <Button
                type="primary"
                size="large"
                onClick={onContinue}
                style={{ minWidth: 160 }}
              >
                Үргэлжлүүлэх →
              </Button>
            </div>
          )}
        </Space>
      </section>
    </Space>
  );
};
